import type { InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from "react";
import { useId } from "react";
import { cn } from "../../lib/cn";

interface FieldShellProps {
  label: string;
  hint?: string | undefined;
  error?: string | undefined;
  optional?: boolean | undefined;
  children: (id: string, describedBy?: string) => ReactNode;
}

function FieldShell({ label, hint, error, optional, children }: FieldShellProps) {
  const id = useId();
  const descriptionId = hint || error ? `${id}-description` : undefined;
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
        {optional ? <span className="field__optional">Optional</span> : null}
      </label>
      {children(id, descriptionId)}
      {hint || error ? (
        <p id={descriptionId} className={cn("field__hint", error && "field__hint--error")}>
          {error ?? hint}
        </p>
      ) : null}
    </div>
  );
}

interface InputFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  hint?: string | undefined;
  error?: string | undefined;
  optional?: boolean | undefined;
}

export function InputField({ label, hint, error, optional, className, ...props }: InputFieldProps) {
  return (
    <FieldShell label={label} hint={hint} error={error} optional={optional}>
      {(id, describedBy) => (
        <input
          id={id}
          aria-describedby={describedBy}
          aria-invalid={Boolean(error)}
          className={cn("input", className)}
          {...props}
        />
      )}
    </FieldShell>
  );
}

interface TextareaFieldProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  hint?: string | undefined;
  error?: string | undefined;
  optional?: boolean | undefined;
}

export function TextareaField({ label, hint, error, optional, className, ...props }: TextareaFieldProps) {
  return (
    <FieldShell label={label} hint={hint} error={error} optional={optional}>
      {(id, describedBy) => (
        <textarea
          id={id}
          aria-describedby={describedBy}
          aria-invalid={Boolean(error)}
          className={cn("textarea", className)}
          {...props}
        />
      )}
    </FieldShell>
  );
}
